/**
 * The custom tool, built on a canvas. Cards are laid out from the draft and never dragged, so the
 * flow always reads left to right; the building happens on the cards: drag a field onto an argument,
 * set a value in its slot, open a step's outputs to find a field. A wire let go over empty canvas
 * opens that argument's picker, and a selected wire can be removed.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
	applyEdgeChanges,
	applyNodeChanges,
	Background,
	BackgroundVariant,
	Controls,
	Panel,
	ReactFlow,
	ReactFlowProvider,
	useConnection,
	useReactFlow,
	type Connection,
	type Edge,
	type EdgeChange,
	type EdgeTypes,
	type IsValidConnection,
	type Node,
	type NodeChange,
	type NodeMouseHandler,
	type NodeTypes,
	type OnConnectEnd
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Workflow } from 'lucide-react';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import type { CustomToolStep } from '@/types/custom-tool.types';
import { useCustomToolBuilder } from '../builder/builder-context';
import { LIMITS } from '../custom-tool-draft';
import { FirstTool } from '../steps/first-tool';
import { SIDE_PANEL_WIDTH } from '../ui/side-panel';
import { CanvasContext, type ArgRef } from './canvas-context';
import { canDraw, wireOf, withoutWire, withWire } from './connections';
import { neighbourhoodOf, withEdgeEmphasis, withNodeEmphasis } from './emphasis';
import { flowGraph, type WireData } from './flow-layout';
import { argOfPort, INPUTS_NODE, stepIdOfNode, stepNodeId } from './ports';
import { AddNode } from './nodes/add-node';
import { ResultNode } from './nodes/result-node';
import { InputsNode } from './nodes/inputs-node';
import { StepNode } from './nodes/step-node';
import { stepFields } from '../steps/step-fields';
import { WireEdge } from './wire-edge';

const nodeTypes: NodeTypes = { inputs: InputsNode, step: StepNode, add: AddNode, result: ResultNode };
const edgeTypes: EdgeTypes = { wire: WireEdge };

const FIT = { padding: 0.15, maxZoom: 1, duration: 250 } as const;
const PANEL_GAP = 24;
const DELETE_KEYS = ['Backspace', 'Delete'];
const CONNECTION_LINE = { stroke: 'var(--color-indigo-500)', strokeWidth: 1.75 };

const Legend = ({ tone, children }: { tone: string; children: string }) => (
	<span className='flex items-center gap-1.5'>
		<span className={cn('h-0.5 w-4 rounded-full', tone)} aria-hidden />
		{children}
	</span>
);

const Key = () => (
	<div className='flex items-center gap-3 rounded-lg border border-gray-300 bg-white/90 px-2.5 py-1.5 text-[11px] leading-4 text-gray-800 backdrop-blur-sm'>
		<Legend tone='bg-sky-500'>From the AI</Legend>
		<Legend tone='bg-violet-500'>From a step</Legend>
		<Legend tone='bg-gray-600'>Runs after</Legend>
	</div>
);

const EmptyStart = () => (
	<div className='pointer-events-none absolute inset-0 flex items-center justify-center'>
		<FirstTool
			icon={Workflow}
			hint="Wire one step's result into another, and that step waits for it."
			className='pointer-events-auto w-[340px]'
		/>
	</div>
);

/** The layout's new positions, keeping the sizes React Flow already measured. */
const withMeasured = (next: Node[], current: Node[]): Node[] => {
	const measured = new Map(current.map((node) => [node.id, node.measured]));
	return next.map((node) => ({ ...node, measured: measured.get(node.id) }));
};

const Canvas = () => {
	const { draft, change, fieldsOf, focus, focusOn, testing } = useCustomToolBuilder();
	const { resolvedTheme } = useTheme();
	const rf = useReactFlow();
	const container = useRef<HTMLDivElement>(null);
	const [picking, setPicking] = useState<ArgRef | null>(null);
	// Cards grow as they open; the layout stacks them by their measured heights.
	const [heights, setHeights] = useState<Record<string, number>>({});
	const empty = draft.steps.length === 0;
	const graph = useMemo(
		() =>
			flowGraph({
				steps: draft.steps,
				inputs: draft.inputs,
				canAdd: draft.steps.length < LIMITS.steps,
				heightOf: (id) => heights[id]
			}),
		[draft.steps, draft.inputs, heights]
	);
	const [nodes, setNodes] = useState<Node[]>(graph.nodes);
	const [edges, setEdges] = useState<Edge[]>(graph.edges);
	useEffect(() => setNodes((current) => withMeasured(graph.nodes, current)), [graph.nodes]);
	// A card that opens up rebuilds the wires; the one selected stays selected.
	useEffect(
		() =>
			setEdges((current) => {
				const selected = new Set(current.filter((edge) => edge.selected).map((edge) => edge.id));
				return graph.edges.map((edge) => (selected.has(edge.id) ? { ...edge, selected: true } : edge));
			}),
		[graph.edges]
	);
	// A picker belongs to the steps it was opened on; once they change, none stays waiting to open.
	useEffect(() => setPicking(null), [draft.steps]);

	// Refit when a card changes column (steps added, removed or rewired), not when one opens up. A new
	// card is fitted once it is measured.
	const fitPending = useRef(true);
	const columns = graph.nodes.map((node) => `${node.id}@${node.position.x}`).join(',');
	useEffect(() => {
		fitPending.current = true;
		// Two frames, so React Flow holds the new positions before fitting them.
		requestAnimationFrame(() =>
			requestAnimationFrame(() => {
				if (!rf.getNodes().every((node) => node.measured?.height)) return;
				fitPending.current = false;
				void rf.fitView(FIT);
			})
		);
	}, [columns, rf]);
	const onNodesChange = (changes: NodeChange[]) => {
		setNodes((current) => applyNodeChanges(changes, current));
		const measured = changes.flatMap((item) => (item.type === 'dimensions' && item.dimensions ? [[item.id, item.dimensions.height] as const] : []));
		if (!measured.length) return;
		setHeights((current) => (measured.every(([id, height]) => current[id] === height) ? current : { ...current, ...Object.fromEntries(measured) }));
		if (fitPending.current) {
			fitPending.current = false;
			// One frame for the measured heights to place the cards, one to fit them.
			requestAnimationFrame(() => requestAnimationFrame(() => void rf.fitView(FIT)));
		}
	};
	// Wires come from the draft; React Flow only tracks which one is selected.
	const onEdgesChange = (changes: EdgeChange[]) =>
		setEdges((current) => applyEdgeChanges(changes.filter((item) => item.type === 'select'), current));
	const onEdgesDelete = (deleted: Edge[]) =>
		change((current) => deleted.reduce((next, edge) => (edge.data ? withoutWire(next, edge.data as WireData) : next), current));

	const focusedNode = focus?.kind === 'step' ? stepNodeId(focus.id) : focus?.kind === 'inputs' ? INPUTS_NODE : null;

	// The hovered card, else the clicked one, lights up with what it is wired to. Nothing fades while
	// a wire is being drawn, so every place it can land stays in view.
	const [hovered, setHovered] = useState<string | null>(null);
	const drawing = useConnection((connection) => connection.inProgress);
	const activeNode = drawing ? null : (hovered ?? focusedNode);
	// A card removed under the mouse never reports the mouse leaving, so only a card still drawn counts.
	useEffect(() => {
		if (hovered && !nodes.some((node) => node.id === hovered)) setHovered(null);
	}, [hovered, nodes]);
	const around = useMemo(
		() => (activeNode && nodes.some((node) => node.id === activeNode) ? neighbourhoodOf(activeNode, edges) : null),
		[activeNode, nodes, edges]
	);
	const shownNodes = useMemo(() => withNodeEmphasis(nodes, around), [nodes, around]);
	const shownEdges = useMemo(() => withEdgeEmphasis(edges, around), [edges, around]);
	const hover: NodeMouseHandler = (_event, node) => setHovered(node.type === 'step' || node.id === INPUTS_NODE ? node.id : null);

	// Bring the focused card into view, clear of the test panel, keeping the zoom.
	useEffect(() => {
		const node = focusedNode ? rf.getNode(focusedNode) : undefined;
		const width = container.current?.clientWidth ?? 0;
		const height = container.current?.clientHeight ?? 0;
		// A refit is on its way and brings every card into view.
		if (!node || !width || fitPending.current) return;
		const { x, y, zoom } = rf.getViewport();
		const cardWidth = node.measured?.width ?? node.width ?? 0;
		const cardHeight = node.measured?.height ?? 0;
		const panel = testing.open ? SIDE_PANEL_WIDTH + PANEL_GAP : 0;
		const left = node.position.x * zoom + x;
		const top = node.position.y * zoom + y;
		const inView = left >= PANEL_GAP && left + cardWidth * zoom <= width - panel - PANEL_GAP && top >= 0 && top + cardHeight * zoom <= height;
		if (inView) return;
		const centreX = node.position.x + cardWidth / 2 + panel / 2 / zoom;
		const centreY = node.position.y + Math.min(cardHeight, (height - 2 * PANEL_GAP) / zoom) / 2;
		void rf.setCenter(centreX, centreY, { zoom, duration: 250 });
	}, [focusedNode, rf, testing.open]);

	const fieldOf = (step: CustomToolStep, name: string) => stepFields(step, fieldsOf(step) ?? []).find((field) => field.name === name);
	const isValidConnection: IsValidConnection = (connection) => {
		const wire = wireOf(connection);
		return !!wire && canDraw(draft, wire);
	};
	const connect = (connection: Connection) => {
		const wire = wireOf(connection);
		if (wire) change((current) => withWire(current, wire, fieldOf));
	};
	// React Flow lets clicks through to a card only when it has a click handler of its own.
	const focusCard: NodeMouseHandler = (_event, node) => {
		const stepId = stepIdOfNode(node.id);
		if (stepId) focusOn({ kind: 'step', id: stepId });
		else if (node.id === INPUTS_NODE) focusOn({ kind: 'inputs' });
	};
	const onConnectEnd: OnConnectEnd = (_event, state) => {
		const stepId = stepIdOfNode(state.fromHandle?.nodeId);
		const field = argOfPort(state.fromHandle?.id);
		// A fixed value is set in its box, so it has no picker to open.
		const holdsValue = draft.steps.find((step) => step.id === stepId)?.args[field ?? '']?.source === 'value';
		if (!state.isValid && !state.toHandle && stepId && field && !holdsValue) setPicking({ stepId, field });
	};

	return (
		<CanvasContext.Provider value={{ picking, setPicking }}>
			<div ref={container} className='relative min-h-0 flex-1'>
				<ReactFlow
					colorMode={resolvedTheme === 'dark' ? 'dark' : 'light'}
					nodes={empty ? [] : shownNodes}
					edges={empty ? [] : shownEdges}
					onNodesChange={onNodesChange}
					onEdgesChange={onEdgesChange}
					onEdgesDelete={onEdgesDelete}
					nodeTypes={nodeTypes}
					edgeTypes={edgeTypes}
					onNodeClick={focusCard}
					onNodeMouseEnter={hover}
					onNodeMouseLeave={() => setHovered(null)}
					onPaneClick={() => focusOn(null)}
					onConnect={connect}
					onConnectEnd={onConnectEnd}
					isValidConnection={isValidConnection}
					connectionRadius={24}
					connectionLineStyle={CONNECTION_LINE}
					nodesDraggable={false}
					edgesReconnectable={false}
					deleteKeyCode={DELETE_KEYS}
					minZoom={0.3}
					maxZoom={1.5}
					panOnScroll
					zoomOnDoubleClick={false}
					proOptions={{ hideAttribution: true }}
				>
					<Background variant={BackgroundVariant.Dots} gap={20} size={1.5} color='var(--color-gray-600)' />
					{!empty && <Controls position='bottom-left' showInteractive={false} fitViewOptions={FIT} />}
					{!empty && (
						<Panel position='bottom-right'>
							<Key />
						</Panel>
					)}
				</ReactFlow>
				{empty && <EmptyStart />}
			</div>
		</CanvasContext.Provider>
	);
};

export const FlowCanvas = () => (
	<ReactFlowProvider>
		<div className='flex min-h-0 flex-1 flex-col bg-gray-50'>
			<Canvas />
		</div>
	</ReactFlowProvider>
);
